<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const rules = defineModel({ type: Array, default: () => [] });

const { t } = useI18n();

const ACTION_STYLES = {
  mark: { icon: 'i-lucide-tag', classes: 'bg-n-amber-3 text-n-amber-11' },
  resolve: {
    icon: 'i-lucide-circle-check',
    classes: 'bg-n-blue-3 text-n-blue-11',
  },
  block: { icon: 'i-lucide-ban', classes: 'bg-n-ruby-3 text-n-ruby-11' },
};

const actionOptions = computed(() =>
  ['mark', 'resolve', 'block'].map(value => ({
    value,
    label: t(`AI_MARKUP.SPAM.ACTIONS.${value.toUpperCase()}`),
  }))
);

const TYPE_ICONS = {
  regex: 'i-lucide-regex',
  flood: 'i-lucide-repeat',
  llm: 'i-ri-sparkling-2-line',
};

const dialogRef = ref(null);
const editingIndex = ref(-1);
const form = ref({ name: '', pattern: '', action: 'mark' });

const patternError = computed(() => {
  if (!form.value.pattern) return '';
  try {
    // eslint-disable-next-line no-new
    new RegExp(form.value.pattern, 'i');
    return '';
  } catch (error) {
    return t('AI_MARKUP.SPAM.INVALID_REGEX');
  }
});

const canSave = computed(
  () =>
    form.value.name.trim() && form.value.pattern.trim() && !patternError.value
);

const mandatoryRules = computed(() =>
  rules.value
    .map((rule, index) => ({ rule, index }))
    .filter(({ rule }) => rule.mandatory)
);
const customRules = computed(() =>
  rules.value
    .map((rule, index) => ({ rule, index }))
    .filter(({ rule }) => !rule.mandatory)
);

const openAdd = () => {
  editingIndex.value = -1;
  form.value = { name: '', pattern: '', action: 'mark' };
  dialogRef.value?.open();
};

const openEdit = index => {
  editingIndex.value = index;
  const { name, pattern, action } = rules.value[index];
  form.value = { name, pattern: pattern || '', action: action || 'mark' };
  dialogRef.value?.open();
};

const save = () => {
  if (!canSave.value) return;
  const entry = {
    name: form.value.name.trim(),
    pattern: form.value.pattern.trim(),
    action: form.value.action,
  };
  if (editingIndex.value === -1) {
    rules.value = [
      ...rules.value,
      {
        id: `custom_${Date.now()}`,
        type: 'regex',
        mandatory: false,
        enabled: true,
        ...entry,
      },
    ];
  } else {
    rules.value = rules.value.map((rule, index) =>
      index === editingIndex.value ? { ...rule, ...entry } : rule
    );
  }
  dialogRef.value?.close();
};

const update = (index, changes) => {
  rules.value = rules.value.map((rule, i) =>
    i === index ? { ...rule, ...changes } : rule
  );
};

const remove = index => {
  rules.value = rules.value.filter((_, i) => i !== index);
};

const ruleTypeLabel = rule => {
  const key = `AI_MARKUP.SPAM.TYPES.${String(rule.type || 'regex').toUpperCase()}`;
  return t(key) === key ? rule.type : t(key);
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <span
        class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
      >
        {{ t('AI_MARKUP.SPAM.MANDATORY') }}
      </span>
      <div
        class="flex flex-col overflow-hidden border divide-y rounded-lg border-n-weak divide-n-weak"
      >
        <div
          v-for="{ rule, index } in mandatoryRules"
          :key="rule.id || index"
          class="flex items-center gap-3 px-3 py-2.5 bg-n-alpha-1"
        >
          <span
            v-tooltip.top="t('AI_MARKUP.SPAM.LOCKED')"
            class="grid flex-shrink-0 rounded-md size-7 place-items-center bg-n-alpha-2 text-n-slate-11"
          >
            <span class="i-lucide-lock size-3.5" />
          </span>
          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium truncate text-n-slate-12">{{
              rule.name
            }}</span>
            <span
              class="inline-flex items-center gap-1 text-[11px] text-n-slate-10 min-w-0"
            >
              <span
                class="flex-shrink-0 size-3"
                :class="TYPE_ICONS[rule.type] || TYPE_ICONS.regex"
              />
              <span class="flex-shrink-0">{{ ruleTypeLabel(rule) }}</span>
              <span v-if="rule.pattern" class="font-mono truncate">
                {{ rule.pattern }}
              </span>
            </span>
          </div>
          <span
            class="inline-flex items-center flex-shrink-0 gap-1 h-6 px-2 rounded-md text-[11px] font-semibold"
            :class="ACTION_STYLES[rule.action]?.classes"
          >
            <span class="size-3" :class="ACTION_STYLES[rule.action]?.icon" />
            {{
              t(`AI_MARKUP.SPAM.ACTIONS.${String(rule.action).toUpperCase()}`)
            }}
          </span>
          <Switch model-value class="opacity-50 pointer-events-none" />
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between gap-3">
        <span
          class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('AI_MARKUP.SPAM.CUSTOM') }}
        </span>
        <Button
          :label="t('AI_MARKUP.SPAM.ADD')"
          icon="i-lucide-plus"
          size="sm"
          variant="faded"
          @click="openAdd"
        />
      </div>
      <div
        v-if="customRules.length"
        class="flex flex-col overflow-hidden border divide-y rounded-lg border-n-weak divide-n-weak"
      >
        <div
          v-for="{ rule, index } in customRules"
          :key="rule.id || index"
          class="flex items-center gap-3 px-3 py-2.5 group hover:bg-n-alpha-1"
          :class="{ 'opacity-60': !rule.enabled }"
        >
          <span
            class="grid flex-shrink-0 rounded-md size-7 place-items-center bg-n-iris-3 text-n-iris-11"
          >
            <span class="i-lucide-regex size-3.5" />
          </span>
          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium truncate text-n-slate-12">{{
              rule.name
            }}</span>
            <span class="font-mono text-[11px] truncate text-n-slate-10">{{
              rule.pattern
            }}</span>
          </div>
          <Select
            :model-value="rule.action"
            :options="actionOptions"
            class="flex-shrink-0 [&_select]:!py-1 [&_select]:!text-xs"
            @update:model-value="value => update(index, { action: value })"
          />
          <Switch
            :model-value="rule.enabled"
            @update:model-value="value => update(index, { enabled: value })"
          />
          <div class="flex gap-0.5 opacity-60 group-hover:opacity-100">
            <Button
              v-tooltip.top="t('AI_MARKUP.EDIT')"
              icon="i-lucide-pencil"
              size="xs"
              variant="ghost"
              color="slate"
              @click="openEdit(index)"
            />
            <Button
              v-tooltip.top="t('AI_MARKUP.DELETE')"
              icon="i-lucide-trash-2"
              size="xs"
              variant="ghost"
              color="ruby"
              @click="remove(index)"
            />
          </div>
        </div>
      </div>
      <p
        v-else
        class="px-3 py-4 m-0 text-sm text-center border border-dashed rounded-lg border-n-weak text-n-slate-10"
      >
        {{ t('AI_MARKUP.SPAM.EMPTY') }}
      </p>
    </div>

    <Dialog
      ref="dialogRef"
      :title="
        editingIndex === -1
          ? t('AI_MARKUP.SPAM.ADD_TITLE')
          : t('AI_MARKUP.SPAM.EDIT_TITLE')
      "
      :confirm-button-label="t('AI_MARKUP.SAVE_ITEM')"
      :disable-confirm-button="!canSave"
      width="lg"
      @confirm="save"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="form.name"
          :label="t('AI_MARKUP.SPAM.NAME')"
          :placeholder="t('AI_MARKUP.SPAM.NAME_PLACEHOLDER')"
        />
        <Input
          v-model="form.pattern"
          :label="t('AI_MARKUP.SPAM.PATTERN')"
          :placeholder="t('AI_MARKUP.SPAM.PATTERN_PLACEHOLDER')"
          :message="patternError || t('AI_MARKUP.SPAM.PATTERN_HINT')"
          :message-type="patternError ? 'error' : 'info'"
          custom-input-class="font-mono"
        />
        <div class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('AI_MARKUP.SPAM.ACTION') }}
          </span>
          <Select v-model="form.action" :options="actionOptions" />
        </div>
      </div>
    </Dialog>
  </div>
</template>
